import { HOST } from 'consts/env';
import { EditDetailsPermissionSubTabType } from 'containers/Common/EditDetailsModal';
import { useApi } from 'hooks/useAPI';
import {
  CheckProjectNameRequest,
  CheckProjectNameResponse,
} from 'types/apis/project/checkProjectName';
import {
  ClearProjectCacheRequest,
  ClearProjectCacheResponse,
} from 'types/apis/project/clearProjectCache';
import {
  ConnectDatasetProjectRequest,
  ConnectDatasetProjectResponse,
} from 'types/apis/project/connectDatasetProject';
import { CopyProjectRequest, CopyProjectResponse } from 'types/apis/project/copyProject';
import { CreateProjectRequest, CreateProjectResponse } from 'types/apis/project/createProject';
import { GetColumnInfoResponse } from 'types/apis/project/getColumnInfo';
import { GetDatasetInfoResponse } from 'types/apis/project/getDatasetInfo';
import { GetPermissionProjectResponse } from 'types/apis/project/getPermissionProject';
import { GetProjectListRequest, GetProjectListResponse } from 'types/apis/project/getProjectList';
import { GetProjectMetaInfoResponse } from 'types/apis/project/getProjectMetaInfo';
import { HasProjectDatasetResponse } from 'types/apis/project/hasProjectDataset';
import { PublishProjectRequest, PublishProjectResponse } from 'types/apis/project/publishProject';
import {
  PublishProjectWithLensRequest,
  PublishProjectWithLensResponse,
} from 'types/apis/project/publishProjectWithLens';
import { RefreshDatasetRequest, RefreshDatasetResponse } from 'types/apis/project/refreshDataset';
import { UpdatePermissionProjectRequest } from 'types/apis/project/updatePermissionProject';
import { UpdateProjectRequest, UpdateProjectResponse } from 'types/apis/project/updateProject';
import { Util } from 'utils';

export const useProjectAPI = () => {
  const api = useApi();

  /** 프로젝트 데이터셋 할당 여부를 확인하는 API */
  const hasProjectDataset = (projectId: string, projectVersion?: string) =>
    api.get<HasProjectDatasetResponse>(
      `${HOST}/project/check/${projectId}/${projectVersion ?? '-1'}`
    );

  /** 프로젝트 목록을 가져오는 API */
  const getProjectList = (params: GetProjectListRequest) =>
    api.get<GetProjectListResponse>(`${HOST}/project/list`, params);

  /** 프로젝트의 컬럼 목록을 가져오는 API */
  const getColumnInfo = (libraryDetailId: string, signal?: AbortSignal) =>
    api.get<GetColumnInfoResponse>(`${HOST}/columns/${libraryDetailId}`, undefined, signal);

  const getColumnInfoList = (libraryDetailIds: string[]) =>
    api.post(`${HOST}/columns`, libraryDetailIds);

  /** 프로젝트 복사 API */
  const copyProject = (form: CopyProjectRequest) =>
    api.post<CopyProjectResponse>(`${HOST}/project/duplicate`, form);

  /** 프로젝트 생성 API */
  const createProject = (form: CreateProjectRequest) =>
    api.post<CreateProjectResponse>(`${HOST}/project/create`, form);

  /** 프로젝트 정보 수정 API */
  const updateProject = (form: UpdateProjectRequest) =>
    api.patch<UpdateProjectResponse>(`${HOST}/project/update`, form);

  /** 프로젝트 삭제 API */
  const deleteProject = (projectId: string) => api.delete(`${HOST}/project/${projectId}`);

  /** 프로젝트 명을 가져오는 API */
  const checkProjectName = (params: CheckProjectNameRequest) =>
    api.get<CheckProjectNameResponse>(`${HOST}/project/check-name`, params);

  /** 프로젝트 초기 데이터셋 할당 API */
  const connectDatasetProject = (form: ConnectDatasetProjectRequest) =>
    api.post<ConnectDatasetProjectResponse>(`${HOST}/project/dataset`, form);

  /** 프로젝트 게시 API */
  const publishProject = (form: PublishProjectRequest) =>
    api.post<PublishProjectResponse>(`${HOST}/library/publish`, form);

  /** 렌즈 포함 퍼블리시 게시 API */
  const publishProjectWithLens = (form: PublishProjectWithLensRequest, stepId: string) =>
    api.post<PublishProjectWithLensResponse>(`${HOST}/library/publish/lens/${stepId}`, form);

  /** 프로젝트에 할당된 데이터셋의 정보를 가져오는 API */
  const getDatasetInfo = (projectId: string, projectVersion: string, signal?: AbortSignal) =>
    api.get<GetDatasetInfoResponse>(
      `${HOST}/project/refresh/file-details/${projectId}/${projectVersion}`,
      undefined,
      signal
    );

  /** 프로젝트 데이터셋 리프레쉬 API */
  const refreshDataset = (form: RefreshDatasetRequest) =>
    api.post<RefreshDatasetResponse>(
      `${HOST}/project/refresh/${form.projectVersionData.projectId}/${form.projectVersionData.projectVersion}`,
      form
    );

  /** 프로젝트 메타데이터 정보를 가져오는 API */
  const getProjectMetaInfo = (projectId: string) =>
    api.get<GetProjectMetaInfoResponse>(`${HOST}/project/meta-info/${projectId}`);

  /** 프로젝트 권한 업데이트 API */
  const updatePermissionProject = (form: UpdatePermissionProjectRequest) =>
    api.patch(`${HOST}/project/share`, form);

  /** 프로젝트 권한 정보를 가져오는 API */
  const getPermissionProject = (projectId: string, type: EditDetailsPermissionSubTabType) =>
    api.get<GetPermissionProjectResponse>(`${HOST}/project/share/${projectId}?type=${type}`);

  /** 프로젝트 캐시 초기화 API */
  const clearProjectCache = (form: ClearProjectCacheRequest) =>
    api.post<ClearProjectCacheResponse>(`${HOST}/project/clear-cache`, {
      ...form,
      inputProjectName: Util.normalizeSearch(form.inputProjectName),
    });

  return {
    hasProjectDataset,
    getProjectList,
    getColumnInfo,
    getColumnInfoList,
    copyProject,
    createProject,
    updateProject,
    deleteProject,
    checkProjectName,
    connectDatasetProject,
    publishProject,
    publishProjectWithLens,
    getDatasetInfo,
    refreshDataset,
    getProjectMetaInfo,
    updatePermissionProject,
    getPermissionProject,
    clearProjectCache,
  };
};
